import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { useTranslation } from "react-i18next";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import styles from "./styles.module.scss";
import PrimaryButton from "../../../../../../components/common/PrimaryButton";
import TextInput from "../../../../../../components/forms/textInput";
import PhoneRuleInput from "../../../../../../components/forms/phoneRuleInput";
import Modal from "../../../../../../components/common/Modal";
import Spinner from "../../../../../../components/common/Spinner";
import { getPhoneRuleConfigs } from "../../../../../../setup/api/services/user";
import {
  FALLBACK_PHONE_RULES,
  PhoneRule,
  isValidFullNumber,
} from "../../../../../../setup/util/phoneRules";
import { FormError } from "../../../../../../setup/util/apiErrors";

export type Inputs = {
  id: number;
  firstName: string;
  lastName: string;
  /** Full number with the calling code, e.g. +966512345678. */
  phoneNumber: string;
  email: string;
  permissions: string[];
};

type FieldName = "firstName" | "lastName" | "phoneNumber" | "email";
const FORM_FIELDS: FieldName[] = [
  "firstName",
  "lastName",
  "phoneNumber",
  "email",
];

interface IAddUserModalProps {
  modalOpen: boolean;
  loading: boolean;
  handleClose: () => void;
  handleSave: (data: Inputs) => void;
  mode: "add" | "edit";
  initialData?: Inputs | null;
  /** Refusals from the last save: field errors go under the field, the rest in a banner. */
  serverErrors?: FormError[];
}

export const PERMISSION_OPTIONS = [
  { id: "CLIENT_ADMIN", label: "كل الصلاحيات" },
  {
    id: "CLIENT_ADMIN_CREATE_CONTRACT",
    label: "إنشاء مشروع",
    note: "يشمل عرض المشاريع",
  },
  {
    id: "CLIENT_ADMIN_UPDATE_CONTRACT",
    label: "تعديل مشروع",
    note: "يشمل عرض المشاريع",
  },
  { id: "CLIENT_ADMIN_VIEW_CONTRACT", label: "عرض المشاريع" },
  { id: "CLIENT_ADMIN_CREATE_USER", label: "إضافة مسئول" },
  { id: "CLIENT_DEVELOPER", label: "مطوّر (بوابة المطورين وعملاء الـ API)" },
];

const VIEW_PERMISSION = "CLIENT_ADMIN_VIEW_CONTRACT";
/** Creating or editing a project is useless without seeing it, so these include viewing. */
const PERMISSIONS_INCLUDING_VIEW = [
  "CLIENT_ADMIN_CREATE_CONTRACT",
  "CLIENT_ADMIN_UPDATE_CONTRACT",
];

/** The permissions with "view projects" added when create or edit is among them. */
export const withImpliedView = (permissions: string[]): string[] =>
  permissions.some((p) => PERMISSIONS_INCLUDING_VIEW.includes(p)) &&
  !permissions.includes(VIEW_PERMISSION)
    ? [...permissions, VIEW_PERMISSION]
    : permissions;

/** Full access and developer access are handed out only by a full admin or the owner. */
const PRIVILEGED_PERMISSIONS = ["CLIENT_ADMIN", "CLIENT_DEVELOPER"];

const DEFAULT_PERMISSIONS = ["CLIENT_ADMIN_VIEW_CONTRACT"];

const emptyForm: Inputs = {
  id: 0,
  firstName: "",
  lastName: "",
  phoneNumber: "",
  email: "",
  permissions: DEFAULT_PERMISSIONS,
};

// The rules rarely change; load them once per page load.
let cachedRules: PhoneRule[] | null = null;

export default function AddUserModal({
  modalOpen,
  handleClose,
  handleSave,
  mode,
  initialData,
  loading,
  serverErrors,
}: IAddUserModalProps) {
  const { t } = useTranslation();
  const [rules, setRules] = useState<PhoneRule[]>(
    cachedRules ?? FALLBACK_PHONE_RULES
  );
  // Staff may grant every permission; the service checks who can grant what.
  const canGrantPrivileged = true;

  useEffect(() => {
    if (!modalOpen || cachedRules) return;
    getPhoneRuleConfigs()
      .then((response) => {
        const enabled = (response?.data?.configs ?? []).filter(
          (config) => config.enabled
        );
        cachedRules = enabled.length > 0 ? enabled : FALLBACK_PHONE_RULES;
        setRules(cachedRules);
      })
      .catch(() => {
        // The service stays the authority; without the list the dialog offers Saudi numbers.
        cachedRules = FALLBACK_PHONE_RULES;
        setRules(cachedRules);
      });
  }, [modalOpen]);

  // Editing changes permissions only: the name, phone and email are shown but not editable, so
  // they are not checked (accounts created before the last name was required have none).
  const isEdit = mode === "edit";
  const schema = z.object({
    id: z.number(),
    firstName: isEdit
      ? z.string()
      : z
          .string()
          .trim()
          .min(1, t("err_required"))
          .max(50, t("err_max_chars", { max: 50 })),
    lastName: isEdit
      ? z.string()
      : z
          .string()
          .trim()
          .min(1, t("err_required"))
          .max(50, t("err_max_chars", { max: 50 })),
    phoneNumber: isEdit
      ? z.string()
      : z.string().refine((value) => isValidFullNumber(rules, value), {
          message: t("err_invalid_phone_rules"),
        }),
    email: isEdit ? z.string() : z.string().trim().email(t("err_invalid_email")),
    permissions: z.array(z.string()).min(1, t("err_select_permission")),
  });

  const {
    control,
    reset,
    watch,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm<Inputs>({
    // Validated when the form is submitted (like the self-registration form), then live.
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(schema),
    defaultValues: emptyForm,
  });

  useEffect(() => {
    if (mode === "edit" && initialData) {
      reset(initialData);
      const normalized = withImpliedView(initialData.permissions ?? []);
      if (normalized.length !== (initialData.permissions ?? []).length) {
        // Created with create/edit but without view: show view ticked and let it be saved.
        setValue("permissions", normalized, { shouldDirty: true });
      }
    } else {
      reset(emptyForm);
    }
  }, [mode, initialData, reset, setValue, modalOpen]);

  // Show what the service refused: on the field it names, or in the banner below.
  const [bannerErrors, setBannerErrors] = useState<string[]>([]);
  useEffect(() => {
    const banner: string[] = [];
    (serverErrors ?? []).forEach((error) => {
      const field = error.field === "authorities" ? "permissions" : error.field;
      if (field && FORM_FIELDS.includes(field as FieldName)) {
        setError(field as FieldName, { type: "server", message: error.message });
      } else if (field === "permissions") {
        banner.push(error.message);
      } else {
        banner.push(error.message);
      }
    });
    setBannerErrors(banner);
  }, [serverErrors, setError]);

  const permissions = watch("permissions") || [];

  const isDisabled = (permId: string) =>
    (PRIVILEGED_PERMISSIONS.includes(permId) && !canGrantPrivileged) ||
    // View stays ticked while create or edit is ticked.
    (permId === VIEW_PERMISSION &&
      permissions.some((p) => PERMISSIONS_INCLUDING_VIEW.includes(p)));

  const togglePermission = (permId: string) => {
    if (isDisabled(permId)) return;
    const options = { shouldValidate: true, shouldDirty: true };

    // "All permissions" selects (or clears) everything it is allowed to select.
    if (permId === "CLIENT_ADMIN") {
      if (permissions.includes(permId)) {
        setValue("permissions", [], options);
      } else {
        setValue(
          "permissions",
          PERMISSION_OPTIONS.map((perm) => perm.id),
          options
        );
      }
      return;
    }

    if (permissions.includes(permId)) {
      // Dropping any single permission means it is no longer "all".
      setValue(
        "permissions",
        permissions.filter((p) => p !== permId && p !== "CLIENT_ADMIN"),
        options
      );
    } else {
      setValue(
        "permissions",
        withImpliedView([...permissions, permId]),
        options
      );
    }
  };

  const submit = handleSubmit((data) => {
    setBannerErrors([]);
    clearErrors();
    handleSave(data);
  });

  return (
    <Modal
      visible={modalOpen}
      onCancel={() => {
        handleClose();
        reset();
      }}
      customModalClassName={styles.modal}
      customModalContainerClassName={styles.modalContainer}>
      <div className={styles.container}>
        <h1 className={styles.title}>
          {mode === "edit" ? t("lbl_edit_user") : t("lbl_add_user")}
        </h1>

        {mode === "add" && (
          <p className={styles.hint}>{t("lbl_add_user_invite_hint")}</p>
        )}

        {bannerErrors.length > 0 && (
          <div className={styles.errorBanner} role="alert">
            {bannerErrors.map((message) => (
              <p key={message}>{message}</p>
            ))}
          </div>
        )}

        {/* FIRST NAME */}
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              disabled={mode === "edit"}
              placeholder={t("lbl_first_name")}
              errorMessage={errors?.firstName?.message}
              required
            />
          )}
        />

        {/* LAST NAME */}
        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              disabled={mode === "edit"}
              placeholder={t("lbl_last_name")}
              errorMessage={errors?.lastName?.message}
              required
            />
          )}
        />

        {/* PHONE NUMBER: country from the phone rules, then the national number */}
        <Controller
          name="phoneNumber"
          control={control}
          render={({ field }) => (
            <PhoneRuleInput
              rules={rules}
              value={field.value}
              onChange={field.onChange}
              disabled={mode === "edit"}
              placeholder={t("lbl_mobile_number")}
              countryLabel={t("lbl_country")}
              errorMessage={errors?.phoneNumber?.message}
              invalidMessage={t("err_invalid_phone_rules") as string}
            />
          )}
        />

        {/* EMAIL */}
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              disabled={mode === "edit"}
              placeholder={t("email")}
              errorMessage={errors?.email?.message}
              required
            />
          )}
        />

        {/* PERMISSIONS */}
        <div className={styles.permissionsWrapper}>
          <h2 className={styles.permissionsTitle}>{t("lbl_permissions")}</h2>

          {PERMISSION_OPTIONS.map((perm) => (
            <label
              key={perm.id}
              className={styles.permissionRow}
              title={
                isDisabled(perm.id)
                  ? (t("lbl_permission_owner_only") as string)
                  : undefined
              }
              style={isDisabled(perm.id) ? { opacity: 0.5 } : undefined}>
              <input
                type="checkbox"
                checked={permissions.includes(perm.id)}
                disabled={isDisabled(perm.id)}
                onChange={() => togglePermission(perm.id)}
              />
              <span>
                {perm.label}
                {"note" in perm && perm.note ? ` (${perm.note})` : ""}
              </span>
            </label>
          ))}
          {errors?.permissions?.message && (
            <p className={styles.fieldError}>{errors.permissions.message}</p>
          )}
        </div>

        <div className={styles.submitButton}>
          <PrimaryButton
            disabled={loading || (mode === "edit" && !isDirty)}
            style={{
              background:
                mode === "edit" && !isDirty ? "#EEF0F5" : "#0D44AD",
            }}
            onClick={() => {
              submit();
            }}>
            {mode === "add" && !loading ? (
              t("lbl_slider_add_bank_btn")
            ) : mode === "edit" && !loading ? (
              t("lbl_edit")
            ) : (
              <Spinner />
            )}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
}
